import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import { useQueryBuilder } from "../hooks/useQueryBuilder";
import { useQueryFilterDraft } from "../hooks/useQueryFilterDraft";
import QueryBuilderHero from "../components/queryBuilder/QueryBuilderHero";
import QueryPresetRow from "../components/queryBuilder/QueryPresetRow";
import QueryFilterBar from "../components/queryBuilder/QueryFilterBar";
import QueryActiveFilterChips from "../components/queryBuilder/QueryActiveFilterChips";
import QueryFiltersDialog from "../components/queryBuilder/QueryFiltersDialog";
import QueryFilterPanel from "../components/queryBuilder/QueryFilterPanel";
import QueryResultsPanel from "../components/queryBuilder/QueryResultsPanel";
import NflIncludeCflToggle from "../components/nfl/NflIncludeCflToggle";
import {
  buildActiveFilterChips,
  toggleStatSelection,
  updateStatFilter,
} from "../utils/queryBuilder";

const QueryBuilder = () => {
  const navigate = useNavigate();
  const {
    sport,
    catalog,
    queryParams,
    queryResults,
    availableTeams,
    activePresetId,
    shareNotice,
    isExecuting,
    isLoadingTeams,
    queryError,
    nflLeagueScope,
    handleShareSearch,
    applyAndRun,
    handleTeamsChange,
    handleRecommendedToggle,
    handleDismissChip,
    handleClearAll,
    handleYearRangeCommit,
    handleGroupChange,
    handleQueryTypeChange,
    applyPresetAndRun,
    dismissShareNotice,
    handleSortModelChange,
    handleLimitChange,
  } = useQueryBuilder();

  const draft = useQueryFilterDraft({
    appliedParams: queryParams,
    onConfirm: applyAndRun,
    sport,
    catalog,
  });

  const activeChips = buildActiveFilterChips(queryParams, catalog);

  const handlePlayerClick = (playerId) => {
    if (playerId) navigate(catalog.getPlayerPath(playerId));
  };

  const handleApplyPreset = (preset) => {
    applyPresetAndRun(preset);
  };

  const handleDraftStatToggle = (stat) => {
    draft.updateDraft((prev) => toggleStatSelection(prev, stat));
  };

  const handleDraftStatFilterChange = (stat, field, value) => {
    draft.updateDraft((prev) => updateStatFilter(prev, stat, field, value));
  };

  return (
    <Box sx={{ pb: 4 }}>
      <QueryBuilderHero subtitle={catalog.heroSubtitle} />

      <QueryPresetRow
        activePresetId={activePresetId}
        featuredPresets={catalog.featuredPresets}
        presets={catalog.presets}
        onApply={handleApplyPreset}
      />

      <QueryFilterBar
        queryParams={queryParams}
        catalog={catalog}
        availableTeams={availableTeams}
        isLoadingTeams={isLoadingTeams}
        activeFilterCount={activeChips.length}
        onYearCommit={handleYearRangeCommit}
        onTeamsCommit={handleTeamsChange}
        onQueryTypeChange={handleQueryTypeChange}
        onGroupChange={handleGroupChange}
        onRecommendedToggle={handleRecommendedToggle}
        onOpenFilters={draft.openFilters}
        onShare={handleShareSearch}
        leagueScopeControl={
          catalog.leagueScopeToggle ? (
            <NflIncludeCflToggle
              includeCfl={nflLeagueScope.includeCfl}
              onToggle={nflLeagueScope.setIncludeCfl}
            />
          ) : null
        }
      />

      <QueryActiveFilterChips
        chips={activeChips}
        onDismiss={handleDismissChip}
        onClearAll={handleClearAll}
      />

      <QueryResultsPanel
        queryParams={queryParams}
        queryResults={queryResults}
        isExecuting={isExecuting}
        queryError={queryError}
        catalog={catalog}
        onPlayerClick={handlePlayerClick}
        onSortModelChange={handleSortModelChange}
        onLimitChange={handleLimitChange}
      />

      <QueryFiltersDialog
        open={draft.isOpen}
        onClose={draft.closeFilters}
        onConfirm={draft.confirmFilters}
        onClearAll={draft.clearDraft}
        showPlayersLabel={draft.showPlayersLabel}
        showPlayersDisabled={draft.showPlayersDisabled}
        isExecuting={isExecuting}
      >
        <QueryFilterPanel
          queryParams={draft.draftParams}
          availableTeams={availableTeams}
          isLoadingTeams={isLoadingTeams}
          catalog={catalog}
          onQueryTypeChange={(value) =>
            draft.updateDraft((prev) => ({ ...prev, queryType: value }))
          }
          onYearChange={(field, value) =>
            draft.updateDraft((prev) => ({ ...prev, [field]: value }))
          }
          onToggleStat={handleDraftStatToggle}
          onStatFilterChange={handleDraftStatFilterChange}
          onBioFilterChange={(field, value) =>
            draft.updateDraft((prev) => ({
              ...prev,
              biographicalFilters: {
                ...prev.biographicalFilters,
                [field]: value,
              },
            }))
          }
          onStatusFilterChange={() => {}}
          onTeamsChange={(teams) => draft.updateDraft({ teams })}
          onSortChange={(sortBy) =>
            draft.updateDraft({ sortBy })
          }
          onSortDirectionChange={(sortDirection) =>
            draft.updateDraft({ sortDirection })
          }
          onRecommendedToggle={draft.toggleRecommended}
        />
      </QueryFiltersDialog>

      <Snackbar
        open={Boolean(shareNotice)}
        autoHideDuration={3000}
        onClose={dismissShareNotice}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={dismissShareNotice} sx={{ width: "100%" }}>
          {shareNotice}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QueryBuilder;
